import type { Thread } from '@wovin/core/thread'
import type { ThreadIDB } from '../data/datalog/local-applog-idb'
import { ThreadInMemory } from '@wovin/core/thread'
import { A } from '@solidjs/router'
import { dateNowIso, EntityID_LENGTH, tsNearlySame, type Applog } from '@wovin/core/applog'
import { purgeHistory } from '@wovin/core/query'
import { tryJsonParse } from '@wovin/utils/conversion'
import { Logger } from 'besonders-logger'
import { Collapse } from 'solid-collapse'
import { useSubscribable } from '@wovin/hooks-solid'
import { createMemo, createSignal, For, Match, Show, Switch } from 'solid-js'
import { tiptapToPlaintext } from '../data/note3-utils-nodeps'
import { useRawThread } from '../ui/reactive'
import { copyOnClick, devMode, explorerUrl, setDevMode } from '../ui/utils-ui'
import { DeleteWithConfirm, DynamicColored, HighlightSameOnHover } from './mini-components'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.DEBUG) // eslint-disable-line unused-imports/no-unused-vars

const MAX_BEFORE_TOGGLE = -50
const MIN_PAUSE_MS = 2 * 60 * 1000 // 2 minutes

function findPurgableApplogs(thread: Thread) {
	const purgableCids = new Set<string>()
	const deleteAttr = ['isDeleted', 'relation/isDeleted', 'block/isDeleted']

	// Find applogs for deleted blocks/relations
	for (const applog of thread.applogs) {
		if (deleteAttr.includes(applog.at) && applog.vl === true) {
			// Collect all applogs for this entity
			for (const log of thread.applogs) {
				if (log.en === applog.en) {
					purgableCids.add(log.cid)
				}
			}
		}
	}

	// Find interim edits: consecutive edits with < 2min pause
	const applogsByEntity = new Map<string, typeof thread.applogs>()
	for (const applog of thread.applogs) {
		if (!applogsByEntity.has(applog.en)) {
			applogsByEntity.set(applog.en, [])
		}
		(applogsByEntity.get(applog.en) as Applog[]).push(applog)
	}

	for (const [, logs] of applogsByEntity) {
		// Sort by timestamp
		const sorted = [...logs].sort((a, b) => a.ts.localeCompare(b.ts))

		for (let i = 0; i < sorted.length - 1; i++) {
			const current = sorted[i]
			const next = sorted[i + 1]

			// Check if same attribute (interim edits)
			if (current.at === next.at) {
				const currentTime = new Date(current.ts).getTime()
				const nextTime = new Date(next.ts).getTime()

				if (nextTime - currentTime < MIN_PAUSE_MS) {
					// This is an interim edit, mark for purge
					purgableCids.add(current.cid)
				}
			}
		}
	}

	return purgableCids
}

// function getPurgableApplogsCount(thread: Thread): number {
// 	return findPurgableApplogs(thread).size
// }

async function doPurgeApplogs(thread: Thread) {
	const purgable = findPurgableApplogs(thread)
	// TODO consider more {`Found ${getPurgableApplogsCount(thread)} purgable applog${getPurgableApplogsCount(thread) !== 1 ? 's' : ''}.\n\nPurge them?`
	if (purgable.size > 0 && 'purge' in thread) {
		const writeableThread = thread as ThreadIDB
		await writeableThread.delete(Array.from(purgable))
	}
}

export function ApplogView({ thread, alwaysOpen }: {
	thread?: Thread
	alwaysOpen?: boolean
} = {}) {
	DEBUG(`<ApplogView>`, { thread, alwaysOpen })
	if (!thread) thread = useRawThread()

	const [showAll, setShowAll] = createSignal(false)
	const [testResult, setTestResult] = createSignal<{
		thread: ThreadInMemory
		purgedCount: number
		keptCount: number
		timeRange: { start: string; end: string }
	} | null>(null)
	const applogs = useSubscribable(thread)

	async function runTestPurge() {
		LOG(`[ApplogView.testPurge] starting — ${applogs().length} applogs in source thread`)

		// Clone into an in-memory thread so we never touch the real IDB
		const copy = ThreadInMemory.fromArray([...applogs()], 'test-purge')

		try {
			const result = await purgeHistory(copy, 'block/content', -1, '-1d')
			LOG(`[ApplogView.testPurge] result`, {
				purgedCount: result.purgedCount,
				keptCount: result.keptCount,
				timeRange: result.timeRange,
				totalAfter: copy.applogs.length,
				blockContentBefore: result.snapshot.get('block/content')?.length ?? 0,
				blockContentAfter: copy.applogs.filter(l => l.at === 'block/content').length,
			})

			setTestResult({
				thread: copy,
				purgedCount: result.purgedCount,
				keptCount: result.keptCount,
				timeRange: result.timeRange,
			})
		} catch (err) {
			ERROR(`[ApplogView.testPurge] failed`, err)
		}
	}

	const reversed = createMemo(() =>
		applogs()
			.slice(showAll() ? undefined : MAX_BEFORE_TOGGLE) // TODO: make it a virtual container - e.g. https://github.com/minht11/solid-virtual-container
			.reverse(),
	)

	return (
		<div id='ApplogView.tsx' w-full overflow-auto text-xs border='1 solid rd-1 gray-500' box-border p-1 color={!alwaysOpen && 'gray-400 hover:text'} {...({} as any)}>
			{
				/* <For each={applogs().reverse()} fallback={<div>No Applogs</div>}>
				{(item, index) => <pre data-index={index()} m-0 text-xs>{JSON.stringify(item)}</pre>}
			</For> */
			}

			<div
				id='ApplogView_TopBar'
				w-full
				text-center
				truncate
				cursor-pointer
				onClick={() => setDevMode(!devMode())}
			>
				<strong>{applogs().length} Applogs</strong>
				&emsp;–&emsp;
				<code>{thread.name}</code>
				&emsp;–&emsp;
				<DeleteWithConfirm
					handler={() => doPurgeApplogs(thread)}
					text='purge'
					confirmText='Try to purge?'
					icon='trash-duotone'
					onClick={(e: MouseEvent) => e.stopPropagation()}
				/>
				&ensp;
				<wa-button
					size='tiny'
					variant='neutral'
					onClick={(e: MouseEvent) => {
						e.stopPropagation()
						runTestPurge()
					}}
				>
					test
				</wa-button>
				<div
					class={'i-ph:caret-down-bold h-5 w-5 float-right ' + `${devMode() ? 'rotate-0' : 'rotate-180'}`}
					duration-300
					transition-property='[transform]'
				/>

			</div>
			<Collapse value={alwaysOpen || devMode()} class='duration-300 transition-property-[height]'>
				<Show when={alwaysOpen || devMode()}>
					<table mt-2 relative right-1 w-full cellpadding='1' style={{ 'border-collapse': 'collapse' }} {...({} as any)}>
						{/* cellspacing='0' cellpadding='0' */}
						<thead>
							<tr text-left font-mono>
								<th class='pr-0.5'>Time</th>
								<th class='px-0.5'>Agent</th>
								<th class='px-0.5'>Entity</th>
								<th class='px-0.5'>Attribute</th>
								<th class='px-0.5'>Value</th>
								<th class='pl-0.5'>CID,Prev</th>
							</tr>
						</thead>
						<tbody>
							<For
								each={reversed()}
								fallback={(
									<tr>
										<td colspan='5'>
											No Applogs
										</td>
									</tr>
								)}
							>
								{(item, index) => {
									const clumpingClass = createMemo(() =>
										reversed()[index() + 1]?.ts && tsNearlySame(reversed()[index() + 1]?.ts, item.ts) ? '' : 'pb-2',
									)
									let valueToDisplay = item.vl
									const valueParsed = typeof item.vl === 'string' ? tryJsonParse(item.vl) : undefined

									if (valueParsed?.type === 'doc') {
										valueToDisplay = tiptapToPlaintext(valueParsed)
									}
									return (
										<tr
											data-index={index()}
											whitespace-nowrap
											font-mono
											bg='hover:black/40'
											data-cid={item.cid}
										>
											<td text-right class={`pr-0.5 ${clumpingClass()}`} title={item.ts} cursor-pointer use:copyOnClick={item.ts}>
												{
													item.ts
														.replace(dateNowIso().slice(0, 11), '') // remove date if it's today
														.slice(0, -5) // remove ms
												}
											</td>
											<td title='TODO: agent info' class={`px-0.5 ${clumpingClass()}`}>
												<HighlightSameOnHover id={item.ag} style={alwaysOpen && 'opacity: 1.0' /* HACK */}>
													<DynamicColored text={item.ag} />
												</HighlightSameOnHover>
											</td>
											<td max-w-5em truncate title={item.en} overflow-hidden class={`px-0.5 ${clumpingClass()}`}>
												<HighlightSameOnHover id={item.en} style={alwaysOpen && 'opacity: 1.0' /* HACK */}>
													<DynamicColored text={item.en} />
												</HighlightSameOnHover>
											</td>
											<td title={item.cid} class={clumpingClass()}>{item.at}</td>
											<td
												title={JSON.stringify(valueParsed || item.vl, undefined, 4)}
												class={`px-0.5 ${clumpingClass()}`}
												max-w-md
												truncate
											>
												<Switch>
													<Match when={typeof valueToDisplay === 'string' && valueToDisplay.length === EntityID_LENGTH}>
														{/* HACK: heuristic to check for EntityID */}
														<HighlightSameOnHover id={valueToDisplay.toString()} style={alwaysOpen && 'opacity: 1.0' /* HACK */}>
															<DynamicColored text={valueToDisplay.toString()} />
														</HighlightSameOnHover>
													</Match>
													<Match when={true}>
														{typeof valueToDisplay === 'string' && valueToDisplay ? valueToDisplay : JSON.stringify(valueToDisplay)}
													</Match>
												</Switch>
											</td>
											<td class={`pl-0.5 ${clumpingClass()}`}>
												<A class='a-unstyled' target='_blank' href={explorerUrl(item.cid)}>
													<HighlightSameOnHover id={item.cid.slice(-8)} style={alwaysOpen && 'opacity: 1.0' /* HACK */}>
														<DynamicColored text={item.cid.slice(-8)} />
													</HighlightSameOnHover>
												</A>
												,
												<Show when={!!item.pv}>
													<A
														class='a-unstyled'
														target='_blank'
														href={explorerUrl(item.pv)}
														title={item.pv && JSON.stringify(thread.getApplog(item.pv), undefined, 2)}
													>
														<HighlightSameOnHover id={item.pv.slice(-8)} style={alwaysOpen && 'opacity: 1.0' /* HACK */}>
															<DynamicColored text={item.pv.slice(-8)} />
														</HighlightSameOnHover>
													</A>
												</Show>
												<Show when={!item.pv}>
													null
												</Show>
											</td>
										</tr>
									)
								}}
							</For>
						</tbody>
					</table>
					<Show when={showAll() || reversed.length < MAX_BEFORE_TOGGLE}>
						<wa-button
							relative
							right-1
							bottom-1
							mt-1
							onClick={() => setShowAll(!showAll())}
						>
							Show {showAll() ? 'fewer' : 'all'}
						</wa-button>
					</Show>
					{/* ── Test-purge result ── */}
					<Show when={testResult()}>
						{(tr) => {
							const resultThread = tr().thread
							const resultApplogs = () => [...resultThread.applogs].reverse()
							return (
								<>
									<hr mt-2 mb-1 border="1 dashed gray-600" />
									<div text-center font-bold text-yellow-300>
										Test purge result: {tr().purgedCount} purged, {tr().keptCount} kept
										&ensp;
										<span text-xs text-gray-400>
											({tr().timeRange.start.slice(0, 10)} … {tr().timeRange.end.slice(0, 10)})
										</span>
									</div>
									<table mt-1 relative right-1 w-full cellpadding="1" style={{ "border-collapse": "collapse" }} {...({} as any)}>
										<thead>
											<tr text-left font-mono text-gray-400>
												<th class="pr-0.5">#</th>
												<th class="px-0.5">Entity</th>
												<th class="px-0.5">Attribute</th>
												<th class="px-0.5">Value</th>
											</tr>
										</thead>
										<tbody>
											<For each={resultApplogs()} fallback={(
												<tr><td colspan="4" text-gray-500>(empty)</td></tr>
											)}>
												{(item, index) => (
													<tr
														data-index={index()}
														whitespace-nowrap
														font-mono
														bg="hover:black/40"
													>
														<td text-right class="pr-0.5">{index() + 1}</td>
														<td max-w-5em truncate title={item.en} overflow-hidden class="px-0.5">
															<DynamicColored text={item.en} />
														</td>
														<td class="px-0.5">{item.at}</td>
														<td class="px-0.5" max-w-md truncate>
															{typeof item.vl === "string" && item.vl ? item.vl : JSON.stringify(item.vl)}
														</td>
													</tr>
												)}
											</For>
										</tbody>
									</table>
								</>
							)
						}}
					</Show>
				</Show>
			</Collapse>
		</div>
	)
}
